/* CB Team Chat Panel — Salon Manager
   File: assets/cb-team-chat-v5.js
   Loaded by: sections/cb-staff-dashboard.liquid
   Backend: SALON-MANAGER AGENT (staff-mode) at https://agent.catherineboden.com/api/webhook/dashboard
            (migrated 2026-06-05 from WF05 /webhook/cb-team-chat — WF05 was returning stale data).
   v3 (2026-06-06): P1 PII gate — attaches `Authorization: Bearer <token>` from window.CB_AUTH
            (minted by PIN login); on 401 calls CB_AUTH.onAuthFail() -> re-PIN.
   v4 (2026-06-08): MOBILE FIX — panel used height:100vh, which on mobile exceeds the visible
            viewport (address bar) and pushed the input box off-screen ("can open chat but not use it").
            Now height:100dvh (dynamic viewport) with a 100vh fallback + flex-shrink:0 on the input row
            so the textarea is always reachable. CSS-only change.
   v5 (2026-06-08): MOBILE FIX 2 — #cb-tc-input (flex textarea) had no min-width:0, so its intrinsic
            content width refused to shrink on a narrow panel and pushed the send button off the right
            edge ("have to scroll right to see send"). Added min-width:0 so the textarea shares the row.
            CSS-only change.
   Contract: POST { message, user:{id,name}, session_id } -> { reply_text, actions_taken, suggestions, escalated }.
   Rollback: revert section ref to cb-team-chat-v4.js (pristine pre-fix copy at _audit_2026-06-08/before/).
*/
(function () {
  'use strict';

  // ---- Config (single source of truth — backend = salon-manager agent staff-mode)
  window.CB_CHAT_CONFIG = window.CB_CHAT_CONFIG || {
    endpoint: 'https://agent.catherineboden.com/api/webhook/dashboard',
    healthEndpoint: null,
    // FIX-162: was 30000 — the agent routinely takes 15-30s+ on tool-heavy staff requests
    // (a simple calendar read measured ~19.5s), so a 30s client timeout aborted real requests
    // and staff saw an "error". 90s gives ample headroom (the agent almost never takes that long).
    timeout: 90000,
    retries: 1,
  };
  window.CB_FEED_CONFIG = window.CB_FEED_CONFIG || {
    endpoint: null,
    pollInterval: 60000,
  };

  var COLORS = {
    aubergine: '#4a1942',
    gold: '#c9a96e',
    cream: '#faf6f0',
    charcoal: '#2A2131',
    creamBorder: '#ece4d4',
    muted: '#8B734A',
    statusGreen: '#3a9d5d',
    statusAmber: '#d49a3a',
    statusRed: '#c44a4a',
  };

  var STYLIST_COLORS = {
    Catherine: '#F5D76E',
    Amber: '#89CFF0',
    'Jess L': '#F4A6C1',
    'Jess S': '#C3A6D8',
    Shane: '#4a1942',
  };

  var SS_STAFF = 'cb_chat_staff_name';
  var SS_SESSION = 'cb_chat_session_id';
  var SS_HISTORY = 'cb_chat_history';

  // ---- Backend interface layer (the ONLY place the endpoint lives)
  // Map the agent's suggestions[] to the drawer's clickable action buttons {label, prompt}.
  function normSuggestions(arr) {
    if (!Array.isArray(arr)) return [];
    return arr.map(function (a) {
      if (typeof a === 'string') return { label: a, prompt: a };
      if (a && a.label) return { label: a.label, prompt: a.prompt || a.label };
      return null;
    }).filter(Boolean);
  }
  async function sendToManager(message, staffName, sessionId) {
    var cfg = window.CB_CHAT_CONFIG;
    // Agent staff endpoint contract: { message, user:{id,name}, session_id }.
    // user.id is stable per staff member (identity/audit); session_id is the conversation thread.
    var staffId = 'staff-' + String(staffName || 'unknown').toLowerCase().replace(/\s+/g, '');
    var payload = {
      message: message,
      user: { id: staffId, name: staffName },
      session_id: sessionId,
    };
    var controller = new AbortController();
    var timer = setTimeout(function () { controller.abort(); }, cfg.timeout || 30000);
    try {
      var resp = await fetch(cfg.endpoint, {
        method: 'POST',
        headers: Object.assign({ 'Content-Type': 'application/json' }, (window.CB_AUTH ? window.CB_AUTH.headers() : {})),
        body: JSON.stringify(payload),
        signal: controller.signal,
      });
      clearTimeout(timer);
      if (resp.status === 401 && window.CB_AUTH) { window.CB_AUTH.onAuthFail(); }
      if (!resp.ok) throw new Error('HTTP ' + resp.status);
      var data = await resp.json();
      return {
        reply: data.reply_text || data.reply || data.response || data.output || '',
        actions: normSuggestions(data.suggestions),
        suggestions: Array.isArray(data.suggestions) ? data.suggestions : [],
      };
    } catch (err) {
      clearTimeout(timer);
      throw err;
    }
  }

  // ---- Storage helpers (sessionStorage — ephemeral by design)
  function loadHistory() {
    try {
      var raw = sessionStorage.getItem(SS_HISTORY);
      return raw ? JSON.parse(raw) : [];
    } catch (e) { return []; }
  }
  function saveHistory(arr) {
    try { sessionStorage.setItem(SS_HISTORY, JSON.stringify(arr)); } catch (e) {}
  }
  function getStaff() { return sessionStorage.getItem(SS_STAFF); }
  function setStaff(name) { sessionStorage.setItem(SS_STAFF, name); }
  function getSessionId() {
    var id = sessionStorage.getItem(SS_SESSION);
    if (!id) {
      var staff = getStaff() || 'unknown';
      id = 'staff-' + staff.toLowerCase().replace(/\s+/g, '') + '-' + Date.now();
      sessionStorage.setItem(SS_SESSION, id);
    }
    return id;
  }

  // ---- Time formatting
  function relTime(ts) {
    var diff = Math.floor((Date.now() - ts) / 1000);
    if (diff < 30) return 'just now';
    if (diff < 60) return diff + 's ago';
    if (diff < 3600) return Math.floor(diff / 60) + ' min ago';
    var d = new Date(ts);
    var hh = String(d.getHours()).padStart(2, '0');
    var mm = String(d.getMinutes()).padStart(2, '0');
    return hh + ':' + mm;
  }

  // ---- Light markdown rendering (bold, line breaks, bullet lists, URLs)
  function escapeHtml(s) {
    return String(s)
      .replace(/&/g, '&amp;')
      .replace(/</g, '&lt;')
      .replace(/>/g, '&gt;')
      .replace(/"/g, '&quot;')
      .replace(/'/g, '&#39;');
  }
  function renderMarkdown(text) {
    if (!text) return '';
    var safe = escapeHtml(text);
    safe = safe.replace(/\*\*([^*]+)\*\*/g, '<strong>$1</strong>');
    // bullet lists: consecutive lines starting with - or • or *
    var lines = safe.split(/\r?\n/);
    var out = [];
    var inList = false;
    for (var i = 0; i < lines.length; i++) {
      var line = lines[i];
      if (/^\s*[-•*]\s+/.test(line)) {
        if (!inList) { out.push('<ul style="margin:6px 0 6px 18px;padding:0;">'); inList = true; }
        out.push('<li>' + line.replace(/^\s*[-•*]\s+/, '') + '</li>');
      } else {
        if (inList) { out.push('</ul>'); inList = false; }
        out.push(line);
      }
    }
    if (inList) out.push('</ul>');
    safe = out.join('\n');
    safe = safe.replace(/\n/g, '<br>');
    safe = safe.replace(/(https?:\/\/[^\s<]+)/g, '<a href="$1" target="_blank" rel="noopener" style="color:' + COLORS.aubergine + ';text-decoration:underline;">$1</a>');
    return safe;
  }

  // ---- DOM construction
  function injectStyles() {
    if (document.getElementById('cb-team-chat-styles')) return;
    var css = ''
      + '#cb-tc-trigger{position:fixed;right:24px;bottom:24px;z-index:9998;width:56px;height:56px;border-radius:50%;background:' + COLORS.aubergine + ';color:' + COLORS.gold + ';border:none;cursor:pointer;box-shadow:0 6px 18px rgba(74,25,66,0.28);display:flex;align-items:center;justify-content:center;transition:transform 0.18s;}'
      + '#cb-tc-trigger:hover{transform:scale(1.06);}'
      + '#cb-tc-trigger svg{width:24px;height:24px;fill:' + COLORS.gold + ';}'
      + '#cb-tc-trigger .cb-tc-badge{position:absolute;top:-2px;right:-2px;min-width:20px;height:20px;border-radius:10px;background:' + COLORS.statusRed + ';color:#fff;font-family:Jost,sans-serif;font-size:11px;font-weight:600;display:none;align-items:center;justify-content:center;padding:0 6px;}'
      + '#cb-tc-backdrop{position:fixed;inset:0;background:rgba(42,33,49,0.4);z-index:9998;opacity:0;pointer-events:none;transition:opacity 0.22s;}'
      + '#cb-tc-backdrop.open{opacity:1;pointer-events:auto;}'
      + '#cb-tc-panel{position:fixed;top:0;right:0;height:100vh;height:100dvh;width:380px;max-width:100vw;background:#fff;z-index:9999;box-shadow:-12px 0 32px rgba(42,33,49,0.18);transform:translateX(100%);transition:transform 0.28s cubic-bezier(.2,.8,.2,1);display:flex;flex-direction:column;font-family:Jost,sans-serif;}'
      + '#cb-tc-panel.open{transform:translateX(0);}'
      + '#cb-tc-header{background:' + COLORS.aubergine + ';color:' + COLORS.gold + ';padding:18px 20px 16px;display:flex;align-items:center;gap:12px;}'
      + '#cb-tc-header .cb-tc-title{font-family:"Cormorant Garamond",serif;font-size:20px;font-weight:500;letter-spacing:0.02em;flex:1;}'
      + '#cb-tc-header .cb-tc-sub{font-family:Jost,sans-serif;font-size:11px;color:' + COLORS.cream + ';opacity:0.78;letter-spacing:0.04em;text-transform:uppercase;margin-top:2px;}'
      + '#cb-tc-status{display:flex;align-items:center;gap:6px;font-size:11px;color:' + COLORS.cream + ';font-weight:300;}'
      + '#cb-tc-status .dot{width:8px;height:8px;border-radius:50%;background:' + COLORS.statusGreen + ';}'
      + '#cb-tc-status.connecting .dot{background:' + COLORS.statusAmber + ';}'
      + '#cb-tc-status.offline .dot{background:' + COLORS.statusRed + ';}'
      + '#cb-tc-close{background:transparent;border:none;color:' + COLORS.gold + ';cursor:pointer;padding:4px;display:flex;align-items:center;}'
      + '#cb-tc-close svg{width:20px;height:20px;fill:currentColor;}'
      + '#cb-tc-body{flex:1;display:flex;flex-direction:column;min-height:0;background:#fbfaf7;}'
      + '#cb-tc-picker{flex:1;display:flex;flex-direction:column;align-items:center;justify-content:center;padding:28px;text-align:center;}'
      + '#cb-tc-picker h3{font-family:"Cormorant Garamond",serif;font-size:22px;font-weight:400;color:' + COLORS.charcoal + ';margin:0 0 6px;}'
      + '#cb-tc-picker p{font-family:Jost,sans-serif;font-size:12px;color:' + COLORS.muted + ';margin:0 0 22px;letter-spacing:0.04em;}'
      + '#cb-tc-picker .picker-grid{display:flex;flex-direction:column;gap:8px;width:100%;max-width:240px;}'
      + '#cb-tc-picker button{padding:12px 16px;background:#fff;border:1px solid ' + COLORS.creamBorder + ';border-radius:4px;font-family:Jost,sans-serif;font-size:13px;font-weight:400;color:' + COLORS.charcoal + ';cursor:pointer;transition:all 0.16s;text-align:left;display:flex;align-items:center;gap:10px;}'
      + '#cb-tc-picker button:hover{background:' + COLORS.cream + ';border-color:' + COLORS.gold + ';}'
      + '#cb-tc-picker .swatch{width:10px;height:10px;border-radius:50%;}'
      + '#cb-tc-messages{flex:1;overflow-y:auto;padding:18px 16px;display:flex;flex-direction:column;gap:10px;}'
      + '.cb-tc-msg{display:flex;flex-direction:column;max-width:82%;}'
      + '.cb-tc-msg.staff{align-self:flex-end;align-items:flex-end;}'
      + '.cb-tc-msg.manager{align-self:flex-start;align-items:flex-start;}'
      + '.cb-tc-bubble{padding:10px 14px;border-radius:14px;font-size:13.5px;line-height:1.5;font-weight:300;}'
      + '.cb-tc-msg.staff .cb-tc-bubble{background:' + COLORS.gold + ';color:' + COLORS.charcoal + ';border-bottom-right-radius:4px;}'
      + '.cb-tc-msg.manager .cb-tc-bubble{background:' + COLORS.cream + ';color:' + COLORS.charcoal + ';border-left:3px solid ' + COLORS.aubergine + ';border-bottom-left-radius:4px;}'
      + '.cb-tc-bubble strong{font-weight:600;}'
      + '.cb-tc-ts{font-size:10px;color:' + COLORS.muted + ';margin-top:4px;letter-spacing:0.04em;padding:0 4px;}'
      + '.cb-tc-actions{display:flex;flex-wrap:wrap;gap:6px;margin-top:6px;}'
      + '.cb-tc-actions button{background:#fff;border:1px solid ' + COLORS.gold + ';color:' + COLORS.aubergine + ';padding:6px 10px;border-radius:14px;font-family:Jost,sans-serif;font-size:11.5px;cursor:pointer;}'
      + '.cb-tc-actions button:hover{background:' + COLORS.cream + ';}'
      + '.cb-tc-typing{display:flex;gap:4px;padding:12px 14px;background:' + COLORS.cream + ';border-radius:14px;border-bottom-left-radius:4px;border-left:3px solid ' + COLORS.aubergine + ';align-self:flex-start;max-width:60px;}'
      + '.cb-tc-typing span{width:6px;height:6px;background:' + COLORS.muted + ';border-radius:50%;animation:cb-tc-bounce 1.2s infinite ease-in-out;}'
      + '.cb-tc-typing span:nth-child(2){animation-delay:0.15s;}'
      + '.cb-tc-typing span:nth-child(3){animation-delay:0.3s;}'
      + '@keyframes cb-tc-bounce{0%,80%,100%{opacity:0.3;transform:translateY(0);}40%{opacity:1;transform:translateY(-3px);}}'
      + '#cb-tc-feed{display:none;border-top:1px solid ' + COLORS.creamBorder + ';background:#fff;padding:8px 14px;font-size:11.5px;}'
      + '#cb-tc-feed.has-items{display:block;}'
      + '#cb-tc-feed-head{display:flex;justify-content:space-between;align-items:center;cursor:pointer;color:' + COLORS.muted + ';font-family:Jost,sans-serif;letter-spacing:0.06em;text-transform:uppercase;padding:4px 0;}'
      + '#cb-tc-feed-list{display:none;max-height:140px;overflow-y:auto;margin-top:6px;}'
      + '#cb-tc-feed.expanded #cb-tc-feed-list{display:block;}'
      + '.cb-tc-feed-item{border-left:2px solid ' + COLORS.aubergine + ';background:' + COLORS.cream + ';padding:6px 10px;margin:4px 0;border-radius:3px;}'
      + '.cb-tc-feed-item .ft{font-size:10px;color:' + COLORS.muted + ';margin-top:2px;}'
      + '#cb-tc-input-wrap{border-top:1px solid ' + COLORS.creamBorder + ';background:#fff;padding:10px 12px;flex-shrink:0;}'
      + '#cb-tc-counter{display:none;text-align:right;font-size:10px;color:' + COLORS.muted + ';margin-bottom:4px;letter-spacing:0.04em;}'
      + '#cb-tc-counter.visible{display:block;}'
      + '#cb-tc-input-row{display:flex;gap:8px;align-items:flex-end;}'
      + '#cb-tc-input{flex:1;min-width:0;border:1px solid ' + COLORS.creamBorder + ';border-radius:18px;padding:10px 14px;font-family:Jost,sans-serif;font-size:13px;font-weight:300;color:' + COLORS.charcoal + ';background:#fafaf6;resize:none;outline:none;max-height:120px;line-height:1.4;}'
      + '#cb-tc-input:focus{border-color:' + COLORS.gold + ';background:#fff;}'
      + '#cb-tc-input:disabled{opacity:0.5;}'
      + '#cb-tc-send{flex-shrink:0;width:38px;height:38px;border-radius:50%;background:' + COLORS.gold + ';color:' + COLORS.aubergine + ';border:none;cursor:pointer;display:flex;align-items:center;justify-content:center;}'
      + '#cb-tc-send:disabled{opacity:0.45;cursor:not-allowed;}'
      + '#cb-tc-send svg{width:16px;height:16px;fill:currentColor;}'
      + '@media (max-width:640px){#cb-tc-panel{width:100vw;height:100vh;height:100dvh;}#cb-tc-header{flex-shrink:0;}#cb-tc-messages{padding:14px 12px;}}'
      ;
    var style = document.createElement('style');
    style.id = 'cb-team-chat-styles';
    style.textContent = css;
    document.head.appendChild(style);
  }

  function buildDom() {
    if (document.getElementById('cb-tc-panel')) return;
    var trigger = document.createElement('button');
    trigger.id = 'cb-tc-trigger';
    trigger.setAttribute('aria-label', 'Open salon manager chat');
    trigger.innerHTML = '<svg viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg"><path d="M20 2H4c-1.1 0-2 .9-2 2v18l4-4h14c1.1 0 2-.9 2-2V4c0-1.1-.9-2-2-2zm0 14H6l-2 2V4h16v12z"/></svg><span class="cb-tc-badge" id="cb-tc-badge">0</span>';

    var backdrop = document.createElement('div');
    backdrop.id = 'cb-tc-backdrop';

    var panel = document.createElement('aside');
    panel.id = 'cb-tc-panel';
    panel.setAttribute('role', 'dialog');
    panel.setAttribute('aria-label', 'Salon Manager');
    panel.innerHTML = ''
      + '<header id="cb-tc-header">'
      + '  <div style="flex:1;">'
      + '    <div class="cb-tc-title" id="cb-tc-title">Salon Manager</div>'
      + '    <div class="cb-tc-sub" id="cb-tc-sub">Internal — staff only</div>'
      + '  </div>'
      + '  <div id="cb-tc-status" class="connecting"><span class="dot"></span><span id="cb-tc-status-label">Connecting</span></div>'
      + '  <button id="cb-tc-close" aria-label="Close"><svg viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg"><path d="M19 6.41L17.59 5 12 10.59 6.41 5 5 6.41 10.59 12 5 17.59 6.41 19 12 13.41 17.59 19 19 17.59 13.41 12z"/></svg></button>'
      + '</header>'
      + '<div id="cb-tc-body">'
      + '  <div id="cb-tc-picker">'
      + '    <h3>Who is chatting?</h3>'
      + '    <p>Identify yourself so the manager can help properly.</p>'
      + '    <div class="picker-grid" id="cb-tc-picker-grid"></div>'
      + '  </div>'
      + '  <div id="cb-tc-messages" style="display:none;"></div>'
      + '  <div id="cb-tc-feed">'
      + '    <div id="cb-tc-feed-head"><span>Recent activity</span><span id="cb-tc-feed-toggle">▾</span></div>'
      + '    <div id="cb-tc-feed-list"></div>'
      + '  </div>'
      + '  <div id="cb-tc-input-wrap" style="display:none;">'
      + '    <div id="cb-tc-counter"></div>'
      + '    <div id="cb-tc-input-row">'
      + '      <textarea id="cb-tc-input" rows="1" placeholder="Ask the salon manager..." maxlength="2000" autocomplete="off"></textarea>'
      + '      <button id="cb-tc-send" aria-label="Send"><svg viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg"><path d="M2.01 21L23 12 2.01 3 2 10l15 2-15 2z"/></svg></button>'
      + '    </div>'
      + '  </div>'
      + '</div>';

    document.body.appendChild(trigger);
    document.body.appendChild(backdrop);
    document.body.appendChild(panel);
  }

  // ---- Picker rendering
  function renderPicker() {
    var grid = document.getElementById('cb-tc-picker-grid');
    if (!grid) return;
    grid.innerHTML = '';
    var names = ['Catherine', 'Amber', 'Jess L', 'Jess S', 'Shane'];
    names.forEach(function (name) {
      var btn = document.createElement('button');
      btn.type = 'button';
      var label = name === 'Shane' ? 'Shane (Admin)' : name;
      btn.innerHTML = '<span class="swatch" style="background:' + (STYLIST_COLORS[name] || COLORS.gold) + ';"></span>' + escapeHtml(label);
      btn.addEventListener('click', function () { onStaffPicked(name); });
      grid.appendChild(btn);
    });
  }

  function onStaffPicked(name) {
    setStaff(name);
    sessionStorage.removeItem(SS_SESSION); // fresh session id per pick
    document.getElementById('cb-tc-picker').style.display = 'none';
    document.getElementById('cb-tc-messages').style.display = 'flex';
    document.getElementById('cb-tc-input-wrap').style.display = 'block';
    document.getElementById('cb-tc-sub').textContent = 'Chatting as ' + name;
    renderHistory();
    if (loadHistory().length === 0) {
      appendMessage({
        role: 'manager',
        text: 'Hi ' + name + '. I am the salon manager. Ask me anything — bookings, the day ahead, a client, the team. I can also pass instructions on to the system.',
        ts: Date.now(),
      });
    }
    setStatus('online');
    setTimeout(function () { document.getElementById('cb-tc-input').focus(); }, 50);
  }

  // ---- Messages
  function appendMessage(msg) {
    var history = loadHistory();
    history.push(msg);
    saveHistory(history);
    renderMessageInto(msg);
    scrollMessagesToBottom();
  }

  function renderMessageInto(msg) {
    var box = document.getElementById('cb-tc-messages');
    if (!box) return;
    var wrap = document.createElement('div');
    wrap.className = 'cb-tc-msg ' + (msg.role === 'staff' ? 'staff' : 'manager');
    var bubble = document.createElement('div');
    bubble.className = 'cb-tc-bubble';
    bubble.innerHTML = renderMarkdown(msg.text);
    wrap.appendChild(bubble);
    if (msg.role === 'manager' && Array.isArray(msg.actions) && msg.actions.length) {
      var actBox = document.createElement('div');
      actBox.className = 'cb-tc-actions';
      msg.actions.forEach(function (a) {
        if (!a || !a.label || !a.prompt) return;
        var b = document.createElement('button');
        b.type = 'button';
        b.textContent = a.label;
        b.addEventListener('click', function () { sendStaffMessage(a.prompt); });
        actBox.appendChild(b);
      });
      wrap.appendChild(actBox);
    }
    var ts = document.createElement('div');
    ts.className = 'cb-tc-ts';
    ts.textContent = relTime(msg.ts);
    ts.setAttribute('data-ts', String(msg.ts));
    wrap.appendChild(ts);
    box.appendChild(wrap);
  }

  function renderHistory() {
    var box = document.getElementById('cb-tc-messages');
    if (!box) return;
    box.innerHTML = '';
    loadHistory().forEach(renderMessageInto);
    scrollMessagesToBottom();
  }

  function scrollMessagesToBottom() {
    var box = document.getElementById('cb-tc-messages');
    if (box) box.scrollTop = box.scrollHeight;
  }

  // Refresh "just now" / "x min ago" timestamps every 30s while panel open
  function refreshTimestamps() {
    var nodes = document.querySelectorAll('#cb-tc-messages .cb-tc-ts');
    nodes.forEach(function (n) {
      var t = parseInt(n.getAttribute('data-ts'), 10);
      if (t) n.textContent = relTime(t);
    });
  }
  setInterval(refreshTimestamps, 30000);

  // ---- Typing indicator
  function showTyping() {
    if (document.getElementById('cb-tc-typing-indicator')) return;
    var box = document.getElementById('cb-tc-messages');
    var el = document.createElement('div');
    el.id = 'cb-tc-typing-indicator';
    el.className = 'cb-tc-typing';
    el.innerHTML = '<span></span><span></span><span></span>';
    box.appendChild(el);
    scrollMessagesToBottom();
  }
  function hideTyping() {
    var el = document.getElementById('cb-tc-typing-indicator');
    if (el) el.parentNode.removeChild(el);
  }

  // ---- Status
  function setStatus(state) {
    var el = document.getElementById('cb-tc-status');
    var lbl = document.getElementById('cb-tc-status-label');
    if (!el || !lbl) return;
    el.classList.remove('connecting', 'offline');
    if (state === 'online') { lbl.textContent = 'Online'; }
    else if (state === 'connecting') { el.classList.add('connecting'); lbl.textContent = 'Sending'; }
    else if (state === 'offline') { el.classList.add('offline'); lbl.textContent = 'Offline'; }
  }

  // ---- Send flow
  async function sendStaffMessage(text) {
    text = (text || '').trim();
    if (!text) return;
    var staff = getStaff();
    if (!staff) return;
    var sessionId = getSessionId();
    appendMessage({ role: 'staff', text: text, ts: Date.now() });
    var input = document.getElementById('cb-tc-input');
    var send = document.getElementById('cb-tc-send');
    input.value = '';
    autoResize(input);
    updateCounter('');
    input.disabled = true;
    send.disabled = true;
    setStatus('connecting');
    showTyping();
    try {
      var result = await sendToManager(text, staff, sessionId);
      hideTyping();
      var replyText = result.reply || 'No reply received.';
      appendMessage({ role: 'manager', text: replyText, ts: Date.now(), actions: result.actions });
      setStatus('online');
    } catch (err) {
      hideTyping();
      var isTimeout = err && (err.name === 'AbortError' || /abort/i.test(err.message || ''));
      var msg = isTimeout
        ? 'That took longer than I could wait for — I may still have done it. Give me a moment and try again, or check the calendar/tasks to confirm.'
        : 'I could not reach the system just now. ' + (err && err.message ? '(' + escapeHtml(err.message) + ')' : '') + ' Please try again in a moment.';
      appendMessage({ role: 'manager', text: msg, ts: Date.now() });
      setStatus('offline');
    } finally {
      input.disabled = false;
      send.disabled = false;
      input.focus();
    }
  }

  // ---- Input UX
  function autoResize(ta) {
    ta.style.height = 'auto';
    ta.style.height = Math.min(ta.scrollHeight, 120) + 'px';
  }
  function updateCounter(val) {
    var counter = document.getElementById('cb-tc-counter');
    if (!counter) return;
    if (val.length > 1500) {
      counter.classList.add('visible');
      counter.textContent = val.length + ' / 2000';
    } else {
      counter.classList.remove('visible');
    }
  }

  // ---- Open / close
  function openPanel() {
    document.getElementById('cb-tc-panel').classList.add('open');
    document.getElementById('cb-tc-backdrop').classList.add('open');
    refreshTimestamps();
    if (getStaff()) {
      document.getElementById('cb-tc-picker').style.display = 'none';
      document.getElementById('cb-tc-messages').style.display = 'flex';
      document.getElementById('cb-tc-input-wrap').style.display = 'block';
      document.getElementById('cb-tc-sub').textContent = 'Chatting as ' + getStaff();
      renderHistory();
      setStatus('online');
      if (window.__cbPendingAsk) { var _pi = document.getElementById('cb-tc-input'); if (_pi) { _pi.value = window.__cbPendingAsk; try { _pi.style.height='auto'; _pi.style.height=Math.min(_pi.scrollHeight,120)+'px'; } catch(e){} } window.__cbPendingAsk = ''; }
      setTimeout(function () { document.getElementById('cb-tc-input').focus(); }, 50);
    } else {
      renderPicker();
    }
    // Hide unread badge on open
    var badge = document.getElementById('cb-tc-badge');
    if (badge) badge.style.display = 'none';
  }
  function closePanel() {
    document.getElementById('cb-tc-panel').classList.remove('open');
    document.getElementById('cb-tc-backdrop').classList.remove('open');
  }

  // ---- Activity feed (hidden when endpoint null)
  function startFeedPolling() {
    if (!window.CB_FEED_CONFIG || !window.CB_FEED_CONFIG.endpoint) return;
    var feed = document.getElementById('cb-tc-feed');
    var head = document.getElementById('cb-tc-feed-head');
    if (head) {
      head.addEventListener('click', function () {
        feed.classList.toggle('expanded');
      });
    }
    async function poll() {
      try {
        var resp = await fetch(window.CB_FEED_CONFIG.endpoint);
        if (!resp.ok) return;
        var data = await resp.json();
        var items = Array.isArray(data) ? data : (data.items || []);
        renderFeed(items);
      } catch (e) {}
    }
    poll();
    setInterval(poll, window.CB_FEED_CONFIG.pollInterval || 60000);
  }
  function renderFeed(items) {
    var feed = document.getElementById('cb-tc-feed');
    var list = document.getElementById('cb-tc-feed-list');
    if (!feed || !list) return;
    if (!items || !items.length) {
      feed.classList.remove('has-items');
      list.innerHTML = '';
      return;
    }
    list.innerHTML = '';
    items.forEach(function (it) {
      var d = document.createElement('div');
      d.className = 'cb-tc-feed-item';
      d.innerHTML = '<div>' + escapeHtml(it.text || '') + '</div><div class="ft">' + escapeHtml(it.time || '') + '</div>';
      list.appendChild(d);
    });
    feed.classList.add('has-items');
  }

  // ---- Wiring + boot
  function isStaffDashboardPage() {
    if (document.getElementById('cb-staff-dashboard-root')) return true;
    if (window.location.pathname && window.location.pathname.indexOf('/pages/staff-dashboard') !== -1) return true;
    return false;
  }
  // No separate unlock check — the dashboard's React PIN gate already protects
  // the dashboard content. The trigger button shows whenever we are on the
  // staff dashboard page; clicking it still requires the staff name picker
  // before any chat can happen.

  function wire() {
    document.getElementById('cb-tc-trigger').addEventListener('click', openPanel);
    document.getElementById('cb-tc-close').addEventListener('click', closePanel);
    document.getElementById('cb-tc-backdrop').addEventListener('click', closePanel);
    var input = document.getElementById('cb-tc-input');
    var send = document.getElementById('cb-tc-send');
    input.addEventListener('input', function () {
      autoResize(input);
      updateCounter(input.value);
    });
    input.addEventListener('keydown', function (e) {
      if (e.key === 'Enter' && !e.shiftKey) {
        e.preventDefault();
        sendStaffMessage(input.value);
      }
    });
    send.addEventListener('click', function () { sendStaffMessage(input.value); });
  }

  function boot() {
    if (!isStaffDashboardPage()) {
      try { console.log('[CB Team Chat] not on staff dashboard, skipping'); } catch (e) {}
      return;
    }
    injectStyles();
    buildDom();
    wire();
    var trigger = document.getElementById('cb-tc-trigger');
    if (trigger) trigger.style.display = 'flex';
    startFeedPolling();
    setStatus('online');
    try { console.log('[CB Team Chat] booted — trigger element:', trigger); } catch (e) {}
  }

  // ---- External hook (v7, 2026-07-15): the dashboard 'Ask salon manager' button hands a task
  // to the manager. Opens the drawer and pre-fills the input with the handle-task request;
  // the team reviews and hits send. Applied via __cbPendingAsk (see openPanel).
  window.cbAskManagerAboutTask = function (text) {
    try {
      window.__cbPendingAsk = String(text || '');
      if (!document.getElementById('cb-tc-panel')) { try { boot(); } catch (e) {} }
      openPanel();
    } catch (e) { try { console.warn('[CB Team Chat] cbAskManagerAboutTask failed', e); } catch (_) {} }
  };

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', boot);
  } else {
    boot();
  }
})();
